/**
 * useFinancingCalculator Hook (Phase 50.1-05)
 *
 * Interactive "what-if" calculator for comparing bank vs seller financing
 * scenarios in real-time without API calls.
 *
 * Features:
 * - Bank scenario (constant baseline)
 * - Seller scenario (dynamic based on user inputs)
 * - Break-even rate calculation using bisection
 * - Delta calculations for comparison
 */

import { useMemo, useCallback } from 'react';

// =============================================================================
// Types
// =============================================================================

export interface UseFinancingCalculatorProps {
  purchasePrice: number;
  noi: number;
  bankRate?: number;      // Default 7%
  bankDownPct?: number;   // Default 25%
  capexBudget?: number;   // CapEx budget for allocation scenarios
  closingCostPct?: number; // Closing costs as % of purchase price (default 3%)
}

export interface BankScenario {
  equity: number;
  loan: number;
  payment: number;        // Monthly payment
  annualDebtService: number;
  coc: number;            // Cash-on-Cash return (decimal)
  rate: number;           // Interest rate (decimal)
}

export interface SellerScenario {
  equity: number;
  note: number;
  payment: number;        // Monthly payment
  annualDebtService: number;
  coc: number;            // Cash-on-Cash return (decimal)
  rate: number;           // Interest rate (decimal)
  downPct: number;        // Down payment percentage (decimal)
  // CapEx allocation fields
  capexAllocationPct: number; // % of down payment allocated to CapEx (decimal)
  sellerEquity: number;       // Down payment going to seller (after CapEx allocation)
  capexInNote: number;        // CapEx amount rolled into seller note
  unfundedCapex: number;      // CapEx not in note (paid from cash at close)
  cashAtClose: number;        // Total cash needed at close (equity + closing costs + unfunded CapEx)
  equitySavings: number;      // Cash saved vs 0% allocation (negative = costs more)
  paymentIncrease: number;    // Monthly payment increase vs 0% allocation
  // Deltas vs bank scenario
  paymentDelta: number;   // Monthly payment difference (negative = seller wins)
  equityDelta: number;    // Equity difference (negative = seller wins)
  cocDelta: number;       // CoC difference (positive = seller wins)
  annualDebtServiceDelta: number;
}

export interface AllocationComparison {
  traditional: SellerScenario;  // 0% allocation (all to seller equity)
  partial: SellerScenario;      // 50% allocation (split between equity and CapEx)
  maxLeverage: SellerScenario;  // 100% allocation (max CapEx in note)
}

export interface UseFinancingCalculatorReturn {
  bankScenario: BankScenario;
  calculateSellerScenario: (downPct: number, rate: number, capexAllocationPct?: number) => SellerScenario;
  calculateAllocationComparison: (downPct: number, rate: number) => AllocationComparison;
  breakEvenRate: number;
  calculateRateFor10PctBetterCoC: () => number;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Calculate monthly loan payment using standard amortization formula.
 * P = L[c(1 + c)^n]/[(1 + c)^n - 1]
 *
 * @param principal - Loan amount
 * @param annualRate - Annual interest rate (decimal, e.g., 0.07 for 7%)
 * @param years - Loan term in years
 * @returns Monthly payment amount
 */
function calculatePayment(principal: number, annualRate: number, years: number): number {
  if (principal <= 0) return 0;
  if (annualRate <= 0) return principal / (years * 12);

  const monthlyRate = annualRate / 12;
  const numPayments = years * 12;

  return (
    principal *
    (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
    (Math.pow(1 + monthlyRate, numPayments) - 1)
  );
}

/**
 * Calculate Cash-on-Cash return.
 * CoC = (NOI - Annual Debt Service) / Equity
 */
function calculateCoC(noi: number, annualDebtService: number, equity: number): number {
  if (equity <= 0) return 0;
  return (noi - annualDebtService) / equity;
}

// =============================================================================
// Main Hook
// =============================================================================

export function useFinancingCalculator({
  purchasePrice,
  noi,
  bankRate = 0.07,
  bankDownPct = 0.25,
  capexBudget = 0,
  closingCostPct = 0.03,
}: UseFinancingCalculatorProps): UseFinancingCalculatorReturn {
  // ==========================================================================
  // Bank Scenario (constant baseline)
  // ==========================================================================
  const bankScenario = useMemo<BankScenario>(() => {
    const equity = purchasePrice * bankDownPct;
    const loan = purchasePrice - equity;
    const payment = calculatePayment(loan, bankRate, 30);
    const annualDebtService = payment * 12;
    const coc = calculateCoC(noi, annualDebtService, equity);

    return {
      equity,
      loan,
      payment,
      annualDebtService,
      coc,
      rate: bankRate,
    };
  }, [purchasePrice, bankDownPct, bankRate, noi]);

  // ==========================================================================
  // Seller Scenario Calculator (dynamic with CapEx allocation)
  // ==========================================================================
  const calculateSellerScenario = useCallback(
    (downPct: number, rate: number, capexAllocationPct: number = 0): SellerScenario => {
      // Step 1: Calculate closing costs
      const closingCosts = purchasePrice * closingCostPct;

      // Step 2: Calculate base down payment and CapEx allocation split
      const baseDownPayment = purchasePrice * downPct;
      const capexAllocation = baseDownPayment * capexAllocationPct;
      const sellerEquity = baseDownPayment - capexAllocation;

      // Step 3: Cap CapEx in note at actual capexBudget
      const capexInNote = Math.min(capexAllocation, capexBudget);
      const unfundedCapex = capexBudget - capexInNote;

      // Step 4: Calculate note including CapEx portion
      // Note = purchase price - seller equity + CapEx in note
      const note = purchasePrice - sellerEquity + capexInNote;
      const payment = calculatePayment(note, rate, 30);
      const annualDebtService = payment * 12;

      // Step 5: Calculate cash at close (equity + closing costs + unfunded CapEx)
      const cashAtClose = sellerEquity + closingCosts + unfundedCapex;

      // Step 6: Calculate CoC using cashAtClose as basis
      // This reflects the true return on actual cash invested
      const coc = calculateCoC(noi, annualDebtService, cashAtClose);

      // Step 7: Calculate comparison metrics vs 0% allocation baseline
      // Baseline: no CapEx allocation (traditional seller financing)
      const baselineNote = purchasePrice - baseDownPayment;
      const baselinePayment = calculatePayment(baselineNote, rate, 30);
      const baselineCashAtClose = baseDownPayment + closingCosts + capexBudget;

      const equitySavings = baselineCashAtClose - cashAtClose; // Positive = less cash needed
      const paymentIncrease = payment - baselinePayment; // Positive = higher payment

      // Legacy equity field for backwards compatibility
      const equity = sellerEquity;

      return {
        equity,
        note,
        payment,
        annualDebtService,
        coc,
        rate,
        downPct,
        // CapEx allocation fields
        capexAllocationPct,
        sellerEquity,
        capexInNote,
        unfundedCapex,
        cashAtClose,
        equitySavings,
        paymentIncrease,
        // Deltas vs bank scenario (negative = seller wins for payment/equity, positive = seller wins for CoC)
        paymentDelta: payment - bankScenario.payment,
        equityDelta: cashAtClose - bankScenario.equity, // Compare actual cash needed
        cocDelta: coc - bankScenario.coc,
        annualDebtServiceDelta: annualDebtService - bankScenario.annualDebtService,
      };
    },
    [purchasePrice, noi, bankScenario, capexBudget, closingCostPct]
  );

  // ==========================================================================
  // Break-Even Rate Calculation
  // ==========================================================================
  /**
   * Find the interest rate at which seller financing monthly payment
   * equals the bank financing monthly payment.
   *
   * Uses bisection method for numerical stability.
   * Assumes 10% down payment for seller (90% seller note).
   */
  const breakEvenRate = useMemo<number>(() => {
    const targetPayment = bankScenario.payment;
    const sellerDownPct = 0.10; // Standard seller financing down payment
    const note = purchasePrice * (1 - sellerDownPct);

    // Bisection bounds
    let low = 0.01;   // 1%
    let high = 0.20;  // 20%
    const tolerance = 0.0001; // 0.01%
    const maxIterations = 50;

    for (let i = 0; i < maxIterations && high - low > tolerance; i++) {
      const mid = (low + high) / 2;
      const payment = calculatePayment(note, mid, 30);

      if (payment < targetPayment) {
        low = mid;
      } else {
        high = mid;
      }
    }

    return (low + high) / 2;
  }, [purchasePrice, bankScenario.payment]);

  // ==========================================================================
  // Calculate Rate for 10% Better CoC
  // ==========================================================================
  /**
   * Find the interest rate that gives 10% better CoC than bank financing.
   * Uses bisection method.
   * Assumes 10% down payment for seller.
   */
  const calculateRateFor10PctBetterCoC = useCallback((): number => {
    const targetCoC = bankScenario.coc * 1.10; // 10% better
    const sellerDownPct = 0.10;
    const equity = purchasePrice * sellerDownPct;
    const note = purchasePrice - equity;

    // CoC = (NOI - Annual Debt Service) / Equity
    // Target Annual Debt Service = NOI - (targetCoC * Equity)
    const targetAnnualDebtService = noi - targetCoC * equity;
    const targetMonthlyPayment = targetAnnualDebtService / 12;

    // If target payment is <= 0, return minimum rate
    if (targetMonthlyPayment <= 0) return 0.04;

    // Bisection to find rate
    let low = 0.01;
    let high = 0.20;
    const tolerance = 0.0001;
    const maxIterations = 50;

    for (let i = 0; i < maxIterations && high - low > tolerance; i++) {
      const mid = (low + high) / 2;
      const payment = calculatePayment(note, mid, 30);

      if (payment > targetMonthlyPayment) {
        high = mid;
      } else {
        low = mid;
      }
    }

    // Clamp to reasonable bounds
    const result = (low + high) / 2;
    return Math.max(0.04, Math.min(0.10, result));
  }, [purchasePrice, noi, bankScenario.coc]);

  // ==========================================================================
  // Allocation Comparison Calculator
  // ==========================================================================
  /**
   * Compare three CapEx allocation strategies for a given down payment and rate.
   * Helps users understand the trade-offs between cash at close and payment.
   */
  const calculateAllocationComparison = useCallback(
    (downPct: number, rate: number): AllocationComparison => {
      return {
        traditional: calculateSellerScenario(downPct, rate, 0.0),   // 0% to CapEx
        partial: calculateSellerScenario(downPct, rate, 0.5),       // 50% to CapEx
        maxLeverage: calculateSellerScenario(downPct, rate, 1.0),   // 100% to CapEx
      };
    },
    [calculateSellerScenario]
  );

  return {
    bankScenario,
    calculateSellerScenario,
    calculateAllocationComparison,
    breakEvenRate,
    calculateRateFor10PctBetterCoC,
  };
}

export default useFinancingCalculator;
